import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Oval } from "react-loader-spinner";

import {
  selectGeneral,
  setShowAuthModal,
} from "../redux/slices/generalSlice";
import Container from "../components/common/Container";
import useLocalStorage from "../hooks/useLocalStorage";

/**
 * LoginRedirect Page
 *
 * Handles `/login?redirect=...` URLs used by the Telegram miniapp
 * FunDeSci verification flow. Opens the auth modal automatically.
 * After successful login, redirects to the specified URL.
 *
 * If the user is already logged in, immediately redirects to the
 * target URL.
 */
const LoginRedirect = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { value: token } = useLocalStorage("token");
  const { isLoggedIn } = useSelector(selectGeneral);

  const redirect = searchParams.get("redirect") || "/";

  useEffect(() => {
    if (token) {
      // Token already in localStorage — redirect immediately without waiting
      // for Redux isLoggedIn (which isn't bootstrapped yet on first render).
      window.location.href = redirect;
    } else {
      // Not logged in — show auth modal so the user can sign in.
      dispatch(setShowAuthModal({ show: true, redirect }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for login completion (user just signed in via the modal)
  useEffect(() => {
    if (isLoggedIn && token && redirect) {
      // Small delay to let token persist to localStorage
      const timer = setTimeout(() => {
        window.location.href = redirect;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, token, redirect]);

  return (
    <Container>
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Sign in to FunDeSci
          </h1>
          <p className="text-gray-600 mb-8">
            Sign in to link your account to Telegram
          </p>

          {!isLoggedIn && (
            <div className="space-y-4">
              <Oval
                height={40}
                width={40}
                color="#6366f1"
                secondaryColor="#a5b4fc"
                strokeWidth={4}
                strokeWidthSecondary={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-gray-500 text-sm">
                The login dialog should appear. If it doesn't,{" "}
                <button
                  onClick={() =>
                    dispatch(setShowAuthModal({ show: true, redirect }))
                  }
                  className="text-indigo-600 underline hover:text-indigo-700"
                >
                  click here
                </button>{" "}
                to open it.
              </p>
            </div>
          )}

          {isLoggedIn && (
            <div>
              <Oval
                height={40}
                width={40}
                color="#22c55e"
                secondaryColor="#bbf7d0"
                strokeWidth={4}
                strokeWidthSecondary={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-green-600 font-medium">
                Logged in! Redirecting...
              </p>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-8">
            Powered by FunDeSci
          </p>
        </div>
      </div>
    </Container>
  );
};

export default LoginRedirect;
